import {
  isChunkOrAssetFailure,
  tryReloadForChunkError
} from '@/services/helpers/ChunkLoadRecovery.js'
import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent'

const NEW_RELIC_TIMEOUT_MS = 5000
const NEW_RELIC_CHECK_INTERVAL_MS = 100
const NEW_RELIC_MAX_ATTEMPTS =
  NEW_RELIC_TIMEOUT_MS / NEW_RELIC_CHECK_INTERVAL_MS

const SCRIPT_ERROR_MESSAGES = ['Script error.', 'Script error'] as const
const RESIZE_OBSERVER_MESSAGE =
  'ResizeObserver loop completed with undelivered notifications.'

// Bot and third-party noise that dominates JavaScriptError ingest (VIP-12034)
const IGNORED_AGENT_ERROR_REGEX = new RegExp(
  [
    'Object Not Found Matching Id', // Microsoft Safe Links scanner
    '_AutofillCallbackHandler', // Instagram/Facebook in-app browser
    'obApi', // Outbrain pixel
    'ResizeObserver loop',
    '^Script error\\.?$'
  ].join('|')
)

declare global {
  interface Window {
    newrelic?: {
      noticeError: (error: Error, extraData?: Record<string, unknown>) => void
      setUserId: (value: string | null) => void
      setErrorHandler?: (
        handler: (error: unknown) => boolean | { group: string }
      ) => void
    }
  }
}

interface NewRelicOptions {
  init: {
    distributed_tracing: { enabled: boolean }
    privacy: { cookies_enabled: boolean }
  }
  info: {
    beacon: string
    errorBeacon: string
    licenseKey: string
    applicationID: string
    sa: number
  }
  loader_config: {
    accountID: string
    trustKey: string
    agentID: string
    licenseKey: string
    applicationID: string
  }
}

function isScriptErrorMessage(msg: string | null): boolean {
  return (
    msg !== null && (SCRIPT_ERROR_MESSAGES as readonly string[]).includes(msg)
  )
}

function isResizeObserverMessage(msg: string | null): boolean {
  return msg === RESIZE_OBSERVER_MESSAGE
}

/**
 * Set the New Relic user id for session grouping. Call when the customer is known (e.g. after login).
 */
export function setNewRelicUserId(customerId: string | number | null): void {
  const setUserId = globalThis.window?.newrelic?.setUserId
  if (!setUserId) return
  setUserId(
    customerId !== null && customerId !== undefined ? String(customerId) : null
  )
}

function waitForNewRelic(): Promise<void> {
  return new Promise((resolve) => {
    let attempts = 0
    const check = (): void => {
      attempts++
      if (globalThis.window?.newrelic?.noticeError) {
        resolve()
        return
      }
      if (attempts >= NEW_RELIC_MAX_ATTEMPTS) {
        console.warn(
          `New Relic failed to load within ${NEW_RELIC_TIMEOUT_MS}ms timeout`
        )
        resolve()
        return
      }
      setTimeout(check, NEW_RELIC_CHECK_INTERVAL_MS)
    }
    check()
  })
}

function setupAgentErrorFilter(): void {
  const setErrorHandler = globalThis.window?.newrelic?.setErrorHandler
  if (!setErrorHandler) return
  // Returning true tells the agent to drop the error
  setErrorHandler((error: unknown) => {
    const message =
      typeof error === 'string'
        ? error
        : String((error as { message?: unknown })?.message ?? '')
    return IGNORED_AGENT_ERROR_REGEX.test(message)
  })
}

function setupGlobalErrorHandlers(): void {
  if (globalThis.window === undefined) return
  globalThis.window.onerror = (message, source, _lineno, _colno, error) => {
    const msg = typeof message === 'string' ? message : null
    if (isScriptErrorMessage(msg) && !source) return true
    if (isResizeObserverMessage(msg)) return true
    if (isChunkOrAssetFailure(error ?? message, source ?? undefined)) {
      // Suppress even when the once-per-session reload is already spent (VIP-12034)
      tryReloadForChunkError()
      return true
    }
    console.error(error ?? message)
    return false
  }
}

/**
 * Loads the New Relic Browser agent (RUM, page load, Ajax). Frontend errors are sent via
 * ApiController.logFrontendError → POST /v2/log/frontend-error only (no Browser agent for errors).
 */
export const loadNewRelic = async (): Promise<void> => {
  if (import.meta.env.VITE_APP_NEW_RELIC_DEV_MODE === 'ON') {
    return
  }

  const licenseKey = `${import.meta.env.VITE_NEW_RELIC_LICENSE_KEY ?? ''}`
  const applicationID = `${import.meta.env.VITE_NEW_RELIC_APPLICATION_ID ?? ''}`
  if (
    !licenseKey ||
    !applicationID ||
    licenseKey === 'undefined' ||
    applicationID === 'undefined'
  ) {
    console.warn(
      'New Relic: missing or invalid VITE_NEW_RELIC_LICENSE_KEY or VITE_NEW_RELIC_APPLICATION_ID, skipping agent init'
    )
    return
  }

  const options: NewRelicOptions = {
    init: {
      distributed_tracing: { enabled: true },
      privacy: { cookies_enabled: true }
    },
    info: {
      beacon: 'bam.nr-data.net',
      errorBeacon: 'bam.nr-data.net',
      licenseKey,
      applicationID,
      sa: 1
    },
    loader_config: {
      accountID: `${import.meta.env.VITE_NEW_RELIC_ACCOUNT_ID ?? ''}`,
      trustKey: `${import.meta.env.VITE_NEW_RELIC_TRUST_KEY ?? ''}`,
      agentID: `${import.meta.env.VITE_NEW_RELIC_AGENT_ID ?? ''}`,
      licenseKey,
      applicationID
    }
  }

  // Agent registers itself on window; reference kept for linter (instantiation is intentional)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _browserAgent = new BrowserAgent(options)
  await waitForNewRelic()
  setupAgentErrorFilter()
  setupGlobalErrorHandlers()
}
